import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { usePresence, useOtherPeers } from "../lib/presence";
import { PresenceAvatar } from "../components/PresenceAvatars";
import type { User } from "../lib/types";
import {
  LESSON_FOUNDATIONS,
  QUIZ_QUESTIONS,
  GLOSSARY,
} from "../data/lessons/foundations";
import { FOUNDATIONS_DIAGRAMS } from "../components/FoundationsDiagrams";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import { Quiz } from "../components/Quiz";
import { Chatbot } from "../components/Chatbot";
import { COURSES } from "../data/courses";
import { I } from "../components/Icons";
import {
  setStepDone,
  setQuizScore,
  setCursor,
  readCursor,
  setCourseComplete,
  useProgress,
} from "../lib/progress";
import { isCourseComplete } from "../lib/course-progress";

interface Props {
  user: User;
  onSignOut: () => void;
}

export function ConceptLesson({ user, onSignOut }: Props) {
  const [, setLocation] = useLocation();
  const onBackToDashboard = () => setLocation("/dashboard");
  const lesson = LESSON_FOUNDATIONS;
  // Resume on the step the learner left off on (clamped to a valid index).
  const [stepIdx, setStepIdx] = useState(() =>
    Math.min(Math.max(readCursor(lesson.courseId), 0), lesson.steps.length - 1),
  );
  const step = lesson.steps[stepIdx];

  // Whether the next course (commits-branches) has been locked by an admin, so
  // the wrap-up "Start course 02" CTA can disable itself and say so.
  const [nextCourseLocked, setNextCourseLocked] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch("/api/courses/locks", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { locked: [] }))
      .then((data: { locked?: string[] }) => {
        if (alive)
          setNextCourseLocked((data.locked ?? []).includes("commits-branches"));
      })
      .catch(() => {
        // Fail open — leave the CTA enabled.
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setStepDone(lesson.courseId, step.id);
    setCursor(lesson.courseId, stepIdx);
  }, [stepIdx, step.id, lesson.courseId]);

  // Explicit completion gates course 02 behind the "Mark course as complete"
  // button on the wrap-up step.
  const progress = useProgress();
  const courseComplete = isCourseComplete(progress, lesson.courseId);

  const presence = usePresence();
  useEffect(() => {
    presence.setLocation({
      screen: "lesson",
      lessonId: lesson.courseId,
      step: stepIdx,
    });
  }, [presence, stepIdx, lesson.courseId]);

  const stepsRef = useRef<HTMLDivElement | null>(null);
  const stepRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [stepOffsets, setStepOffsets] = useState<number[]>([]);

  useLayoutEffect(() => {
    function measure() {
      const offsets = stepRefs.current.map((el) =>
        el ? el.offsetTop + el.offsetHeight / 2 : 0,
      );
      setStepOffsets(offsets);
    }
    measure();
    const ro = new ResizeObserver(measure);
    if (stepsRef.current) ro.observe(stepsRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [lesson.steps.length]);

  const otherPeers = useOtherPeers();
  const peersInLesson = otherPeers.filter(
    (p) =>
      p.location.screen === "lesson" &&
      p.location.lessonId === lesson.courseId &&
      typeof p.location.step === "number",
  );
  const peersByStep = new Map<number, typeof peersInLesson>();
  for (const p of peersInLesson) {
    const s = p.location.step!;
    const arr = peersByStep.get(s) ?? [];
    arr.push(p);
    peersByStep.set(s, arr);
  }

  function goNext() {
    if (stepIdx < lesson.steps.length - 1) setStepIdx(stepIdx + 1);
  }
  function goPrev() {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  }

  const progressPct = Math.round(
    ((stepIdx + 1) / lesson.steps.length) * 100,
  );

  return (
    <div className="gf-shell">
      <Sidebar active="courses" user={user} onSignOut={onSignOut} />
      <div className="gf-main">
        <TopBar
          crumbs={[
            { t: "Git Fit", href: "/dashboard" },
            { t: "My courses", href: "/dashboard" },
            { t: lesson.courseTitle, strong: true },
          ]}
          right={
            <button
              className="gf-btn gf-btn--ghost"
              onClick={onBackToDashboard}
            >
              <I.ChevronLeft size={14} /> All courses
            </button>
          }
        />

        <div className="gf-lesson">
          <div className="gf-lesson__sidebar">
            <div className="gf-lesson__course-meta">
              Course 01 · Lesson 1 of 4
            </div>
            <div className="gf-lesson__course-title">{lesson.title}</div>
            <div className="gf-lesson__course-sub">
              The mental model — no commands yet
            </div>

            <div className="gf-lesson__steps" ref={stepsRef}>
              {lesson.steps.map((s, i) => {
                const cls = ["gf-lesson__step"];
                if (i === stepIdx) cls.push("is-active");
                if (i < stepIdx) cls.push("is-done");
                return (
                  <button
                    key={s.id}
                    ref={(el) => {
                      stepRefs.current[i] = el;
                    }}
                    className={cls.join(" ")}
                    onClick={() => setStepIdx(i)}
                  >
                    <span className="gf-lesson__step-num">
                      {i < stepIdx ? <I.Check size={12} /> : i + 1}
                    </span>
                    <span className="gf-lesson__step-body">
                      <span className="gf-lesson__step-t">{s.shortTitle}</span>
                      <span className="gf-lesson__step-sub">{s.shortSub}</span>
                    </span>
                  </button>
                );
              })}
              {peersInLesson.map((p) => {
                const idx = p.location.step!;
                const top = stepOffsets[idx];
                if (top == null) return null;
                const sameStep = peersByStep.get(idx) ?? [];
                const order = sameStep.findIndex(
                  (q) => q.sessionId === p.sessionId,
                );
                return (
                  <div
                    key={p.sessionId}
                    className="gf-lesson__step-badges"
                    style={{
                      top: top - 12,
                      transform: `translate(${order * -10}px, 0)`,
                    }}
                  >
                    <PresenceAvatar peer={p} size={24} />
                  </div>
                );
              })}
            </div>

            <div className="gf-lesson__progress">
              <div className="gf-lesson__progress-row">
                <span>Lesson progress</span>
                <span className="gf-lesson__progress-val">
                  {progressPct}%
                </span>
              </div>
              <div className="gf-lesson__progress-bar">
                <div
                  className="gf-lesson__progress-bar-fill"
                  style={{ width: `${progressPct}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="gf-lesson__body">
            <div className="gf-step-head">
              <div className="gf-step-head__left">
                <div className="gf-step-head__eyebrow">{step.eyebrow}</div>
                <h1>{step.title}</h1>
                <p className="gf-step-head__sub">{step.sub}</p>
              </div>
              <div className="gf-step-head__nav">
                <button
                  className="gf-btn gf-btn--secondary"
                  onClick={goPrev}
                  disabled={stepIdx === 0}
                >
                  <I.ChevronLeft size={14} /> Back
                </button>
                {stepIdx === lesson.steps.length - 1 && !courseComplete && (
                  // Subtle action beside Next step on the final step. Marking
                  // complete unlocks course 02.
                  <button
                    className="gf-btn gf-btn--ghost"
                    onClick={() => setCourseComplete(lesson.courseId)}
                  >
                    <I.Check size={14} /> Mark course as complete
                  </button>
                )}
                <button
                  className="gf-btn gf-btn--gradient"
                  onClick={goNext}
                  disabled={stepIdx === lesson.steps.length - 1}
                >
                  Next step <I.ArrowRight size={14} />
                </button>
              </div>
            </div>

            {step.kind === "concept" && step.diagram ? (
              <ConceptStepBody diagramKey={step.diagram} />
            ) : step.kind === "wrap" ? (
              <WrapStep
                nextCourseLocked={nextCourseLocked}
                courseComplete={courseComplete}
                onNextCourse={() => setLocation("/lesson/commits-branches")}
              />
            ) : null}
          </div>
        </div>

      </div>
      <Chatbot
        context={{
          screen: "lesson",
          courseId: lesson.courseId,
          courseTitle: lesson.courseTitle,
          courseDesc: COURSES.find((c) => c.id === lesson.courseId)?.desc,
          stepTitle: step.title,
          stepSub: step.sub,
          stepEyebrow: step.eyebrow,
          stepKind: step.kind,
        }}
      />
    </div>
  );
}

function ConceptStepBody({ diagramKey }: { diagramKey: string }) {
  const Diagram = FOUNDATIONS_DIAGRAMS[diagramKey];
  return (
    <div className="gf-step-content gf-step-content--solo">
      {Diagram ? <Diagram /> : null}
    </div>
  );
}

function WrapStep({
  nextCourseLocked,
  courseComplete,
  onNextCourse,
}: {
  nextCourseLocked: boolean;
  courseComplete: boolean;
  onNextCourse: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div className="gf-card">
        <div className="gf-card__head">
          <h3>Knowledge check</h3>
          <div className="gf-card__head-meta">3 quick questions</div>
        </div>
        <div className="gf-card__body">
          <Quiz
            questions={QUIZ_QUESTIONS}
            onScored={(s) => setQuizScore(LESSON_FOUNDATIONS.courseId, s)}
          />
        </div>
      </div>

      <div className="gf-card">
        <div className="gf-card__head">
          <h3>Glossary</h3>
          <div className="gf-card__head-meta">Terms from this course</div>
        </div>
        <div className="gf-card__body">
          <div className="gf-gloss">
            {GLOSSARY.map((g) => (
              <div key={g.term} className="gf-gloss__item">
                <div className="gf-gloss__term">{g.term}</div>
                <div className="gf-gloss__def">{g.def}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          background: "var(--nymbl-navy)",
          borderRadius: 14,
          padding: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ zIndex: 1, position: "relative" }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "var(--nymbl-g-cyan)",
              marginBottom: 8,
            }}
          >
            Up next
          </div>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 24,
              fontWeight: 700,
              margin: 0,
              letterSpacing: "-0.02em",
            }}
          >
            Commits &amp; branches — your first real git
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "rgba(255,255,255,0.65)",
              margin: "6px 0 0",
              maxWidth: 520,
            }}
          >
            Now that you have the mental model, course 2 drops you into a live
            sandbox. You'll make a commit, branch off, and merge — all in the
            browser, no setup required.
          </p>
        </div>
        {/* Forward CTA, gated on completion: the learner marks the course
            complete via the subtle button in the top nav, which enables this. */}
        <button
          className="gf-btn gf-btn--gradient"
          style={{ position: "relative", zIndex: 1 }}
          onClick={onNextCourse}
          disabled={nextCourseLocked || !courseComplete}
          aria-disabled={nextCourseLocked || !courseComplete}
          title={
            nextCourseLocked
              ? "The next course is locked by your instructor."
              : !courseComplete
                ? "Mark this course complete to continue."
                : undefined
          }
        >
          {nextCourseLocked ? (
            <>
              <I.Lock size={14} /> Next course is locked
            </>
          ) : (
            <>
              Start course 02 <I.ArrowRight size={14} />
            </>
          )}
        </button>
        <div
          style={{
            position: "absolute",
            right: -60,
            top: -120,
            width: 320,
            height: 320,
            background: "var(--nymbl-gradient-45)",
            opacity: 0.35,
            filter: "blur(60px)",
            borderRadius: "50%",
          }}
        ></div>
      </div>
    </div>
  );
}
